import * as authService from './authService'

const getBaseURL = () =>
  (import.meta.env.VITE_API_BASE_URL as string) || 'http://localhost:8081'

const CONFIG_SERVICE =
  (import.meta.env.VITE_CONFIGURATION_SERVICE_URL as string) ||
  `${getBaseURL().replace(/\/$/, '')}/api/v1/configuration-service`

export type PointsRule = {
  id: string
  name: string
  inputAmount: number
  outputAmount: number
  enabled: boolean
  displayOrder: number
}

export type RewardType = 'credit' | 'item'

export type RewardAvailabilityReason =
  | 'out_of_stock'
  | 'daily_limit'
  | 'user_daily_limit'
  | null

export type RewardAvailability = {
  rewardId: string
  stockRemaining: number | null
  dailyRemaining: number | null
  userDailyRemaining: number | null
  effectiveRemaining: number | null
  canRedeem: boolean
  unavailableReason: RewardAvailabilityReason
}

export type PointsReward = {
  id: string
  name: string
  description: string
  imageUrl: string
  type: RewardType
  pointsCost: number
  creditAmount: number
  stock: number
  limitPerUserPerDay: number
  limitPerDay: number
  dailyCreditCap: number
  enabled: boolean
  displayOrder: number
}

export type PointsSystemConfig = {
  cashToPointsEnabled: boolean
  pointsToCashEnabled: boolean
  depositToPointsAutoEnabled: boolean
  cashToPointsRatePercent: number
  pointsToCashRatePercent: number
  depositToPointsRatePercent: number
  rewardsEnabled?: boolean
  promotionalMessage?: string
}

export type PointsConfigData = {
  tenantId: string
  system: PointsSystemConfig
  cashToPointsRules: PointsRule[]
  pointsToCashRules: PointsRule[]
  rewards: PointsReward[]
  version: number
}

type PointsConfigResponse = {
  success: boolean
  data?: PointsConfigData
  message?: string
}

type ExchangeDirection = 'cash-to-points' | 'points-to-cash'

type ExchangePointsResponse = {
  success: boolean
  data?: {
    direction: ExchangeDirection
    ruleId: string
    ruleName: string
    cashAmount: number
    pointsAmount: number
    currentPoints: number
    pointsEarned: number
    pointsUsed: number
    walletBalance: number
  }
  message?: string
}

type RedeemRewardResponse = {
  success: boolean
  data?: {
    redemptionId: string
    rewardId: string
    rewardName: string
    rewardType: RewardType
    pointsCost: number
    creditAmount: number
    currentPoints: number
    walletBalance: number | null
    fulfillmentStatus: string | null
  }
  message?: string
}

export const USER_SUMMARY_UPDATED_EVENT = 'user_summary_updated'

const sortRules = (left: PointsRule, right: PointsRule) =>
  Number(left.displayOrder || 0) - Number(right.displayOrder || 0)

const normalizeRule = (rule: Partial<PointsRule> | null | undefined): PointsRule | null => {
  if (!rule?.id) return null
  return {
    id: String(rule.id),
    name: String(rule.name || ''),
    inputAmount: Number(rule.inputAmount || 0),
    outputAmount: Number(rule.outputAmount || 0),
    enabled: rule.enabled !== false,
    displayOrder: Number(rule.displayOrder || 0),
  }
}

const normalizeReward = (reward: Partial<PointsReward> | null | undefined): PointsReward | null => {
  if (!reward?.id) return null
  const type = reward.type === 'credit' ? 'credit' : 'item'
  return {
    id: String(reward.id),
    name: String(reward.name || ''),
    description: String(reward.description || ''),
    imageUrl: String(reward.imageUrl || ''),
    type,
    pointsCost: Number(reward.pointsCost || 0),
    creditAmount: type === 'credit' ? Number(reward.creditAmount || 0) : 0,
    stock: Number(reward.stock ?? -1),
    limitPerUserPerDay: Number(reward.limitPerUserPerDay || 0),
    limitPerDay: Number(reward.limitPerDay || 0),
    dailyCreditCap: Number(reward.dailyCreditCap || 0),
    enabled: reward.enabled !== false,
    displayOrder: Number(reward.displayOrder || 0),
  }
}

const normalizeConfig = (config: PointsConfigData | null | undefined, tenantId: string): PointsConfigData => ({
  tenantId: config?.tenantId || tenantId,
  system: {
    cashToPointsEnabled: config?.system?.cashToPointsEnabled === true,
    pointsToCashEnabled: config?.system?.pointsToCashEnabled === true,
    depositToPointsAutoEnabled: config?.system?.depositToPointsAutoEnabled === true,
    cashToPointsRatePercent: Number(config?.system?.cashToPointsRatePercent || 0),
    pointsToCashRatePercent: Number(config?.system?.pointsToCashRatePercent || 0),
    depositToPointsRatePercent: Number(config?.system?.depositToPointsRatePercent || 0),
    rewardsEnabled: config?.system?.rewardsEnabled !== false,
    promotionalMessage:
      typeof config?.system?.promotionalMessage === 'string'
        ? config.system.promotionalMessage
        : '',
  },
  cashToPointsRules: Array.isArray(config?.cashToPointsRules)
    ? config!.cashToPointsRules.map(normalizeRule).filter(Boolean) as PointsRule[]
    : [],
  pointsToCashRules: Array.isArray(config?.pointsToCashRules)
    ? config!.pointsToCashRules.map(normalizeRule).filter(Boolean) as PointsRule[]
    : [],
  rewards: Array.isArray(config?.rewards)
    ? config!.rewards.map(normalizeReward).filter(Boolean) as PointsReward[]
    : [],
  version: Number(config?.version || 0),
})

const getAuthHeaders = (tenantId: string) => {
  const token = authService.getAccessToken()
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'X-Tenant-ID': tenantId,
    'X-Requested-With': 'XMLHttpRequest',
  }
}

const readJson = async <T>(response: Response): Promise<T> =>
  (await response.json().catch(async () => ({ message: await response.text().catch(() => '') }))) as T

export const getPointsPromotionalMessage = (config: PointsConfigData | null | undefined): string =>
  String(config?.system?.promotionalMessage || '').trim()

export const getActivePointsRules = (rules: PointsRule[] | undefined): PointsRule[] =>
  [...(rules || [])]
    .filter((rule) => rule.enabled !== false && Number(rule.inputAmount) > 0 && Number(rule.outputAmount) > 0)
    .sort(sortRules)

export const getActiveRewards = (rewards: PointsReward[] | undefined): PointsReward[] =>
  [...(rewards || [])]
    .filter((reward) => reward.enabled !== false && Number(reward.pointsCost) > 0)
    .sort((left, right) => Number(left.displayOrder || 0) - Number(right.displayOrder || 0))

export const isPointsFeatureEnabled = (config: PointsConfigData | null): boolean =>
  Boolean(config?.system?.cashToPointsEnabled || config?.system?.pointsToCashEnabled || config?.system?.rewardsEnabled)

export const notifyUserSummaryUpdated = () => {
  if (typeof window === 'undefined') return
  authService.invalidateUserInfoCache()
  window.dispatchEvent(new Event(USER_SUMMARY_UPDATED_EVENT))
  window.dispatchEvent(new Event('balance_notification'))
}

export async function getPointsConfig(tenantId: string): Promise<PointsConfigData | null> {
  try {
    const response = await fetch(`${CONFIG_SERVICE}/points-config/${encodeURIComponent(tenantId)}`, {
      method: 'GET',
      headers: getAuthHeaders(tenantId),
      credentials: 'include',
    })
    const payload = await readJson<PointsConfigResponse>(response)
    if (!response.ok || !payload.success || !payload.data) {
      return null
    }
    return normalizeConfig(payload.data, tenantId)
  } catch (error) {
    console.error('Failed to load points config:', error)
    return null
  }
}

export async function exchangePoints(params: {
  tenantId: string
  userId: string
  direction: ExchangeDirection
  ruleId: string
}): Promise<ExchangePointsResponse> {
  const response = await fetch(
    `${getBaseURL().replace(/\/$/, '')}/api/v1/user-info/${encodeURIComponent(params.tenantId)}/${encodeURIComponent(params.userId)}/points/exchange`,
    {
      method: 'POST',
      headers: getAuthHeaders(params.tenantId),
      credentials: 'include',
      body: JSON.stringify({
        direction: params.direction,
        ruleId: params.ruleId,
      }),
    }
  )
  const payload = await readJson<ExchangePointsResponse>(response)
  if (!response.ok || !payload.success) {
    throw new Error(payload.message || 'ไม่สามารถแลกคะแนนได้')
  }
  return payload
}

type RewardAvailabilityResponse = {
  success: boolean
  data?: {
    rewards: RewardAvailability[]
  }
  message?: string
}

export const getRewardAvailabilityUnavailableLabel = (
  reason: RewardAvailabilityReason
): string => {
  if (reason === 'out_of_stock') return 'รางวัลหมดแล้ว'
  if (reason === 'daily_limit') return 'รางวัลวันนี้หมดแล้ว'
  if (reason === 'user_daily_limit') return 'คุณแลกครบจำนวนของวันนี้แล้ว'
  return 'ไม่สามารถแลกได้'
}

export const buildRewardAvailabilityDisplay = (
  reward: Pick<PointsReward, 'type' | 'stock' | 'limitPerDay' | 'limitPerUserPerDay'>,
  availability?: RewardAvailability | null
): {
  stockBadge: string | null
  stockBadgeTone: 'available' | 'low' | 'sold-out'
  hintLines: string[]
  canRedeem: boolean
  unavailableLabel: string | null
} => {
  if (!availability) {
    return {
      stockBadge: null,
      stockBadgeTone: 'available',
      hintLines: [],
      canRedeem: true,
      unavailableLabel: null,
    }
  }

  const unit = reward.type === 'item' ? 'ชิ้น' : 'สิทธิ์'
  const hintLines: string[] = []
  let stockBadge: string | null = null
  let stockBadgeTone: 'available' | 'low' | 'sold-out' = 'available'

  if (availability.effectiveRemaining !== null) {
    if (availability.effectiveRemaining <= 0) {
      stockBadge = 'หมดแล้ว'
      stockBadgeTone = 'sold-out'
    } else {
      stockBadge = `เหลือ ${availability.effectiveRemaining.toLocaleString('th-TH')} ${unit}`
      stockBadgeTone = availability.effectiveRemaining <= 3 ? 'low' : 'available'
    }
  } else if (Number(reward.stock) >= 0) {
    stockBadge = `เหลือ ${Number(reward.stock).toLocaleString('th-TH')} ${unit}`
    stockBadgeTone = Number(reward.stock) <= 3 ? 'low' : 'available'
  }

  if (
    availability.stockRemaining !== null &&
    availability.dailyRemaining !== null &&
    availability.stockRemaining !== availability.dailyRemaining
  ) {
    hintLines.push(`คงเหลือทั้งหมด ${availability.stockRemaining.toLocaleString('th-TH')} ${unit}`)
    hintLines.push(`วันนี้เหลือ ${availability.dailyRemaining.toLocaleString('th-TH')} ${unit}`)
  } else if (
    availability.dailyRemaining !== null &&
    availability.effectiveRemaining === null
  ) {
    hintLines.push(`วันนี้เหลือ ${availability.dailyRemaining.toLocaleString('th-TH')} ${unit}`)
  }

  if (Number(reward.limitPerUserPerDay) > 0 && availability.userDailyRemaining !== null) {
    hintLines.push(`คุณแลกได้อีก ${availability.userDailyRemaining.toLocaleString('th-TH')} ครั้ง/วัน`)
  }

  return {
    stockBadge,
    stockBadgeTone,
    hintLines,
    canRedeem: availability.canRedeem,
    unavailableLabel: availability.canRedeem
      ? null
      : getRewardAvailabilityUnavailableLabel(availability.unavailableReason),
  }
}

export async function getRewardAvailability(params: {
  tenantId: string
  userId: string
}): Promise<Record<string, RewardAvailability>> {
  const response = await fetch(
    `${getBaseURL().replace(/\/$/, '')}/api/v1/user-info/${encodeURIComponent(params.tenantId)}/${encodeURIComponent(params.userId)}/rewards/availability`,
    {
      method: 'GET',
      headers: getAuthHeaders(params.tenantId),
      credentials: 'include',
    }
  )
  const payload = await readJson<RewardAvailabilityResponse>(response)
  if (!response.ok || !payload.success || !payload.data?.rewards) {
    return {}
  }

  return payload.data.rewards.reduce<Record<string, RewardAvailability>>((map, item) => {
    if (!item?.rewardId) return map
    map[item.rewardId] = item
    return map
  }, {})
}

export async function redeemReward(params: {
  tenantId: string
  userId: string
  rewardId: string
  shippingInfo?: Record<string, unknown> | null
}): Promise<RedeemRewardResponse> {
  const response = await fetch(
    `${getBaseURL().replace(/\/$/, '')}/api/v1/user-info/${encodeURIComponent(params.tenantId)}/${encodeURIComponent(params.userId)}/rewards/redeem`,
    {
      method: 'POST',
      headers: getAuthHeaders(params.tenantId),
      credentials: 'include',
      body: JSON.stringify({
        rewardId: params.rewardId,
        shippingInfo: params.shippingInfo || null,
      }),
    }
  )
  const payload = await readJson<RedeemRewardResponse>(response)
  if (!response.ok || !payload.success) {
    throw new Error(payload.message || 'ไม่สามารถแลกรางวัลได้')
  }
  return payload
}

export type RewardRedemptionFulfillmentStatus =
  | 'not-applicable'
  | 'pending'
  | 'processing'
  | 'shipped'
  | 'delivered'
  | 'cancelled'

export type RewardRedemptionHistoryItem = {
  redemptionId: string
  rewardId: string
  rewardName: string
  rewardType: RewardType
  rewardImageUrl?: string
  pointsCost: number
  creditAmount?: number
  beforePoints?: number | null
  afterPoints?: number | null
  status: 'completed' | 'failed'
  fulfillmentStatus: RewardRedemptionFulfillmentStatus
  shippingInfo?: {
    name?: string
    phone?: string
    address?: string
  } | null
  note?: string | null
  createdAt?: string
  updatedAt?: string
}

type RewardRedemptionListResponse = {
  success: boolean
  data?: RewardRedemptionHistoryItem[]
  pagination?: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
  message?: string
}

export const REWARD_FULFILLMENT_LABELS: Record<RewardRedemptionFulfillmentStatus, string> = {
  'not-applicable': '-',
  pending: 'รอแอดมินดำเนินการ',
  processing: 'กำลังจัดเตรียม',
  shipped: 'จัดส่งแล้ว',
  delivered: 'ส่งมอบแล้ว',
  cancelled: 'ยกเลิก',
}

export async function getMyRewardRedemptions(params: {
  tenantId: string
  userId: string
  page?: number
  limit?: number
}): Promise<RewardRedemptionListResponse> {
  const search = new URLSearchParams()
  if (params.page) search.set('page', String(params.page))
  if (params.limit) search.set('limit', String(params.limit))
  const query = search.toString()

  const response = await fetch(
    `${getBaseURL().replace(/\/$/, '')}/api/v1/user-info/${encodeURIComponent(params.tenantId)}/${encodeURIComponent(params.userId)}/rewards/redemptions${query ? `?${query}` : ''}`,
    {
      method: 'GET',
      headers: getAuthHeaders(params.tenantId),
      credentials: 'include',
    }
  )
  const payload = await readJson<RewardRedemptionListResponse>(response)
  if (!response.ok || !payload.success) {
    throw new Error(payload.message || 'ไม่สามารถโหลดประวัติแลกรางวัลได้')
  }
  return payload
}
